import { forwardRef } from "react";
import { css } from "@emotion/react";
import { placeholder } from "src/lib/emotion/placeholder.ts";
import { BaseInputV2AdornmentContext } from "./BaseInputV2AdornmentContext.tsx";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import type { InputHTMLAttributes, ReactNode } from "react";

export type BaseInputV2Props = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value" | "defaultValue"
> & {
  hasError?: boolean;
  endAdornment?: ReactNode;
  startAdornment?: ReactNode;
  value?: string;
  defaultValue?: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
};

export const BaseInputV2 = forwardRef<HTMLInputElement, BaseInputV2Props>(
  (props, ref) => {
    const {
      name,
      placeholder,
      onChange,
      onBlur,
      onFocus,
      onKeyUp,
      onKeyDown,
      onPaste,
      tabIndex,
      id,
      type,
      disabled = false,
      defaultValue,
      value,
      autoComplete,
      ...directProps
    } = props;

    const proxiedInputProps = {
      name,
      placeholder,
      onChange,
      onBlur,
      onFocus,
      onKeyUp,
      onKeyDown,
      onPaste,
      tabIndex,
      id,
      type,
      disabled,
      defaultValue,
      value,
      autoComplete,
    };

    const {
      hasError = false,
      endAdornment,
      startAdornment,
      inputProps,
      ...restProps
    } = directProps;

    return (
      <div {...restProps} css={rootStyles({ hasError, disabled })}>
        <BaseInputV2AdornmentContext.Provider
          value={{
            position: "start",
            disabled,
          }}
        >
          {startAdornment}
        </BaseInputV2AdornmentContext.Provider>

        <input
          ref={ref}
          css={inputStyles}
          {...proxiedInputProps}
          {...inputProps}
        />

        <BaseInputV2AdornmentContext.Provider
          value={{
            position: "end",
            disabled,
          }}
        >
          {endAdornment}
        </BaseInputV2AdornmentContext.Provider>
      </div>
    );
  },
);

const outline = ({
  hasError,
  theme,
  disabled = false,
}: {
  hasError: boolean;
  disabled?: boolean;
  theme: ExactTheme;
}) => {
  if (disabled) {
    return {
      border: theme.inputBorderV2.solid.disabled.default,
    };
  }
  return {
    border: hasError
      ? theme.inputBorderV2.solid.standard.error
      : theme.inputBorderV2.solid.standard.default,
    boxShadow: "none",

    ["&:hover"]: {
      border: hasError
        ? theme.inputBorderV2.solid.hover.error
        : theme.inputBorderV2.solid.hover.default,
    },
    ["&:focus, &:focus-within"]: {
      border: hasError
        ? theme.inputBorderV2.solid.focused.error
        : theme.inputBorderV2.solid.focused.default,
      outline: "none",
      boxShadow: hasError
        ? `0 0 0 1px ${theme.colorsV2.red[500]}`
        : `0 0 0 1px ${theme.colorsV2.blue[500]}`,
    },
  };
};

const rootStyles =
  ({ hasError, disabled }: { hasError: boolean; disabled: boolean }) =>
  (theme: ExactTheme) => css`
    display: inline-flex;
    width: 100%;
    height: 37px;
    border-radius: ${theme.borderRadiusV2.primary};
    align-items: center;
    background: ${disabled ? theme.colorsV2.grey[50] : theme.colorsV2.white};
    ${outline({
      disabled,
      hasError,
      theme,
    })};
    transition:
      border 150ms,
      box-shadow 150ms,
      color 150ms;
  `;

const inputStyles = ({
  inputColorV2,
  inputV2,
  inputWeightV2,
  inputV2Placeholder,
  inputV2PlaceholderDisabled,
}: ExactTheme) => css`
  display: block;
  height: 100%;
  min-width: 0;
  text-overflow: ellipsis;
  background: transparent;

  ${inputV2};
  color: ${inputColorV2.normal};
  font-weight: ${inputWeightV2.normal};
  flex-grow: 1;

  text-align: inherit;

  ${placeholder(inputV2Placeholder)};

  &:disabled {
    color: ${inputColorV2.disabled};
    font-weight: ${inputWeightV2.disabled};
    cursor: not-allowed;
    ${placeholder(inputV2PlaceholderDisabled)};
  }
  outline: none;
  border: none;
`;
